import {
  Autocomplete,
  Box,
  CircularProgress,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Position } from '@staffedupusa/shared-components';
import { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from 'react';

import CannotFindPositionModal from '@features/employerOnboarding/components/CannotFindPositionModal/CannotFindPositionModal';
import { useGetJobPostings } from '@features/jobPostings/hooks/useGetJobPostings';
import { PositionOption, useGetPositions } from '@features/positions';
import JobPostingModal from '@features/jobPostings/components/JobPostingModal/JobPostingModal';
import { normal } from '@styles/fontWeights';

type AddPositionsStepProps = {
  handleGoBack: () => void;
  handleNext: () => void;
  industryId: number;
  setJobsLength: Dispatch<SetStateAction<number>>;
};

const AddPositionsStep: FC<AddPositionsStepProps> = ({
  handleGoBack,
  handleNext,
  setJobsLength,
  industryId,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [showJobPostingModal, setShowJobPostingModal] = useState(false);
  const [isProTipOpen, setIsProTipOpen] = useState<boolean>(false);
  const [isProTipShown, setIsProTipShown] = useState<boolean>(false);
  const [standardPositionId, setStandardPositionId] = useState<number | null>(
    null,
  );
  const [options, setOptions] = useState<PositionOption[]>([]);
  const [selectedJobPosting, setSelectedJobPosting] = useState<Position | null>(
    null,
  );
  const proTipRef = useRef(null);

  const { jobPostingsData } = useGetJobPostings();

  const { isLoadingPositions } = useGetPositions({
    setPositions: setOptions,
    industryId,
  });

  const handleOptionClick = (option: PositionOption | null) => {
    if (option?.id === 0) {
      setIsDialogOpen(true);
    } else if (option) {
      setShowJobPostingModal(true);
      setStandardPositionId(option.id);
      if (!isProTipShown) {
        setIsProTipOpen(true);
        setIsProTipShown(true);
      }
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  useEffect(() => {
    if (jobPostingsData && jobPostingsData?.object?.length > 0) {
      setJobsLength(jobPostingsData?.object?.length)
      setSelectedJobPosting(jobPostingsData?.object?.[0]);
      setShowJobPostingModal(true);
    }
  }, [jobPostingsData]);
  return (
    <>
      <Stack spacing={3} mb={3} direction="column">
        <Autocomplete
          options={options}
          value={null}
          blurOnSelect
          onChange={(event, value: PositionOption | null) =>
            handleOptionClick(value)
          }
          getOptionLabel={(option) => option.name || ''}
          filterOptions={(opts: PositionOption[], inputState) =>
            opts.filter(
              (option) =>
                option.id === 0 ||
                option.name
                  .toLowerCase()
                  .includes(inputState.inputValue.toLowerCase()),
            )
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Positions"
              variant="outlined"
              ref={proTipRef}
            />
          )}
        />
        <Popover
          open={isProTipOpen}
          anchorEl={proTipRef.current}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          onClose={() => setIsProTipOpen(false)}
          disablePortal
        >
          <Typography sx={{ p: 2, maxWidth: '200px' }}>
            Pro Tip! Adding pay rates will help your jobs get ranked in the
            external boards we post to for you (for free). Even if it’s minimum
            wage, or min+tips, add them now!
          </Typography>
        </Popover>
      </Stack>
      <CannotFindPositionModal
        isOpen={isDialogOpen}
        handleClose={handleDialogClose}
        handleBack={handleGoBack}
      />
      {(standardPositionId || selectedJobPosting) && showJobPostingModal && (
        <JobPostingModal
          isOpen={showJobPostingModal}
          handleNext={handleNext}
          handleClose={() => {
            setShowJobPostingModal(false);
          }}
          jobPosting={selectedJobPosting}
          standardPositionId={standardPositionId}
        />
      )}
    </>
  );
};

export default AddPositionsStep;
